export function postcodeParts(input) {
  const compact = String(input).toUpperCase().replace(/\s/g, '');
  if (!/^(?:[A-Z]{1,2}\d[A-Z\d]?|GIR)\d[A-Z]{2}$/.test(compact)) return null;
  return { outward: compact.slice(0, -3), full: compact.slice(0, -3) + ' ' + compact.slice(-3) };
}

export function deliveryQuote(data, group, postcode, address, unloading, quantity = 1) {
  if (Number(quantity) !== 1) return { error: 'multiple' };
  const parts = postcodeParts(postcode);
  if (!parts) return { error: 'postcode' };
  const tariff = data.tariffs[group];
  if (!tariff) return { error: 'unmapped' };
  const zones = data.zones[tariff.carrier][parts.outward];
  if (!zones || zones.length !== 1) return { error: 'quote' };
  const zone = zones[0];
  const base = tariff.rates[zone];
  if (!Number.isFinite(base)) return { error: 'quote' };
  if (!['Business', 'Residential'].includes(address)) return { error: 'requirements' };
  if (tariff.requiresHiabQuote && ['HIAB', 'Tail lift'].includes(unloading)) return { error: 'hiab' };
  if (tariff.carrier === 'Pallex' && !['Forklift', 'Tail lift'].includes(unloading)) return { error: 'requirements' };
  const option = tariff.carrier === 'DX' ? 'Not applicable' : unloading;
  const extras = (address === 'Residential' ? tariff.residential : 0) + (option === 'Tail lift' ? tariff.tailLift : 0);
  const cents = Math.round(((base + extras) * (1 + tariff.fuel) + Number.EPSILON) * 100);
  return { cents, zone, carrier: tariff.carrier, postcode: parts.full, address, unloading: option, handle: tariff.handle, version: data.version };
}

export function expectedChargePrice(quote, taxIncluded, vat) {
  return taxIncluded ? Math.round(quote.cents * (1 + vat / 100)) : quote.cents;
}

export function selectChargeVariant(product, quote, expectedPrice) {
  return product.variants.find(v => v.option1 === quote.zone && v.option2 === quote.address && v.option3 === quote.unloading && v.available && v.price === expectedPrice);
}

export function selectedCabinetVariant(product, variantId) {
  if (variantId) return product.variants.find(v => String(v.id) === String(variantId));
  return product.variants.length === 1 ? product.variants[0] : undefined;
}

export function pairedItems(model, cabinetVariant, chargeVariant, quote, batch) {
  const common = { 'Delivery postcode': quote.postcode, 'Address type': quote.address, 'Unloading': quote.unloading, '_kingsley_pair': batch, '_kingsley_rates': quote.version };
  return [{ id: cabinetVariant.id, quantity: 1, properties: { ...common, 'Delivery': 'Included in the delivery charge shown in your basket', '_kingsley_cabinet': model.handle, '_kingsley_expected_charge': String(chargeVariant.price) } },
    { id: chargeVariant.id, quantity: 1, properties: { ...common, 'For cabinet': cabinetVariant.sku || model.title, '_kingsley_delivery': 'true' } }];
}

export function mountBuilder(root, data, config, preview = false) {
  const form = root.querySelector('form');
  const modelSelect = form.elements.cabinet;
  const postcode = form.elements.postcode;
  const address = form.elements.address;
  const unloading = form.elements.unloading;
  const quantity = form.elements.quantity;
  const accept = form.elements.accept;
  const lock = form.elements.cabinet_variant;
  const button = form.querySelector('button[type="submit"]');
  const message = root.querySelector('[data-kb-message]');
  const text = config.text;
  const cache = new Map();
  const requested = new URLSearchParams(window.location.search).get('cabinet');
  if (requested && config.models.some(m => m.handle === requested)) modelSelect.value = requested;
  let revision = 0;
  let busy = false;
  let state = null;
  const money = cents => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100);
  const rootURL = config.root || '/';
  async function getProduct(handle) {
    if (preview) return { variants: [{ id: 1, sku: 'PREVIEW', available: true, price: config.models.find(m => m.handle === handle).price }] };
    if (!cache.has(handle)) cache.set(handle, fetch(rootURL + 'products/' + encodeURIComponent(handle) + '.js').then(async r => { if (!r.ok) throw new Error('product'); return r.json(); }).catch(e => { cache.delete(handle); throw e; }));
    return cache.get(handle);
  }
  function showError(key) {
    state = null;
    message.textContent = text[key] || text.quote;
    for (const node of root.querySelectorAll('[data-kb-value]')) node.textContent = '—';
  }
  async function refresh() {
    if (busy) return;
    const current = ++revision;
    button.disabled = true;
    state = null;
    const model = config.models.find(m => m.handle === modelSelect.value);
    if (!model) return showError('choose');
    const tariff = data.tariffs[model.group];
    if (!tariff) return showError('unmapped');
    const isPallet = tariff.carrier === 'Pallex';
    const large = !!tariff.requiresHiabQuote;
    const tailOption = root.querySelector('[data-kb-tail-lift]');
    const hiabOption = root.querySelector('[data-kb-hiab]');
    const hiabNote = root.querySelector('[data-kb-hiab-note]');
    if (tailOption) { tailOption.hidden = large; tailOption.disabled = large; }
    if (hiabOption) { hiabOption.hidden = !large; hiabOption.disabled = !large; }
    if (hiabNote) hiabNote.hidden = !large;
    root.querySelector('[data-kb-unloading]').hidden = !isPallet;
    unloading.required = isPallet;
    const q = deliveryQuote(data, model.group, postcode.value, address.value, unloading.value, quantity.value);
    if (q.error) return showError(q.error);
    message.textContent = text.loading;
    try {
      const product = await getProduct(model.handle);
      if (current !== revision) return;
      const cabinetVariant = selectedCabinetVariant(product, lock ? lock.value : config.variantId);
      if (!cabinetVariant) return showError('configuration');
      if (!cabinetVariant.available) return showError('unavailable');
      const cabinetEx = config.taxIncluded ? Math.round(cabinetVariant.price / (1 + config.vat / 100)) : cabinetVariant.price;
      const chargePrice = expectedChargePrice(q, config.taxIncluded, config.vat);
      const totalEx = cabinetEx + q.cents;
      const totalInc = config.taxIncluded ? cabinetVariant.price + chargePrice : Math.round(totalEx * (1 + config.vat / 100));
      root.querySelector('[data-kb-value="cabinet"]').textContent = money(cabinetEx);
      root.querySelector('[data-kb-value="delivery"]').textContent = money(q.cents);
      root.querySelector('[data-kb-value="total"]').textContent = money(totalEx);
      root.querySelector('[data-kb-value="inc"]').textContent = money(totalInc);
      message.textContent = text.standard;
      if (preview || !config.enabled || !config.confirmed) { message.textContent += ' ' + text.preview; return; }
      const chargeProduct = await getProduct(q.handle);
      if (current !== revision) return;
      const chargeVariant = selectChargeVariant(chargeProduct, q, chargePrice);
      if (!chargeVariant) return showError('configuration');
      state = { model, cabinetVariant, chargeVariant, q };
      button.disabled = busy || !accept.checked;
    } catch (error) { if (current === revision) showError('configuration'); }
  }
  form.addEventListener('input', refresh);
  form.addEventListener('change', refresh);
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (!form.reportValidity() || !state || busy || !config.enabled || !config.confirmed || preview) return;
    busy = true;
    button.disabled = true;
    const selected = state;
    const disabledBefore = new Map([...form.querySelectorAll('input,select')].map(el => [el, el.disabled]));
    form.querySelectorAll('input,select').forEach(el => { el.disabled = true; });
    try {
      const cartResponse = await fetch(rootURL + 'cart.js');
      if (!cartResponse.ok) throw new Error('cart');
      const cart = await cartResponse.json();
      if (cart.items.some(item => item.properties && (item.properties._kingsley_pair || item.properties._kingsley_delivery))) throw new Error('multiple');
      const items = pairedItems(selected.model, selected.cabinetVariant, selected.chargeVariant, selected.q, crypto.randomUUID());
      const response = await fetch(rootURL + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (!response.ok) throw new Error('add');
      window.location.assign(config.cartURL || rootURL + 'cart');
    } catch (error) {
      message.textContent = error.message === 'multiple' ? text.multiple : text.addError;
      busy = false;
      for (const [el, disabled] of disabledBefore) el.disabled = disabled;
      await refresh();
    }
  });
  refresh();
}

if (typeof document !== 'undefined') {
  function openDialog(id) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog.showModal();
    dialog.dataset.oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const postcode = dialog.querySelector('input[name="postcode"]');
    if (postcode) postcode.focus();
  }
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-kb-open]');
    if (opener) { openDialog(opener.dataset.kbOpen); return; }
    const close = event.target.closest('[data-kb-close]');
    if (close) close.closest('dialog').close();
    if (event.target.matches('dialog.kb-dialog')) {
      const box = event.target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) event.target.close();
    }
  });
  async function boot(root) {
    if (root.dataset.kbMounted) return;
    root.dataset.kbMounted = 'true';
    const dialog = root.closest('dialog');
    if (dialog) dialog.addEventListener('close', () => { document.body.style.overflow = dialog.dataset.oldOverflow || ''; });
    const config = JSON.parse(root.querySelector('[data-kb-config]').textContent);
    try {
      const response = await fetch(root.dataset.ratesUrl);
      if (!response.ok) throw new Error('rates');
      mountBuilder(root, await response.json(), config);
    } catch (error) { root.querySelector('[data-kb-message]').textContent = config.text.configuration; }
  }
  document.querySelectorAll('[data-kingsley-builder]').forEach(boot);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-kingsley-builder]').forEach(boot));
}
